import apiClient from './client';

// ==================== TYPES ====================

export interface VAPIDKeyResponse {
  public_key: string | null;
  is_configured: boolean;
}

export interface PushSubscription {
  id: number;
  endpoint: string;
  device_name: string | null;
  created_at: string | null;
  last_used_at: string | null;
}

export interface SubscribeRequest {
  endpoint: string;
  p256dh: string;
  auth: string;
  device_name?: string;
}

export interface TestNotificationResponse {
  status: string;
  message: string;
  sent: number;
  failed: number;
}

// ==================== API FUNCTIONS ====================

export const pushApi = {
  /**
   * Get VAPID public key for push subscription
   */
  getVapidKey: async (): Promise<VAPIDKeyResponse> => {
    const response = await apiClient.get<VAPIDKeyResponse>('/push/vapid-key');
    return response.data;
  },

  /**
   * Subscribe to push notifications
   */
  subscribe: async (subscription: SubscribeRequest): Promise<{ status: string; message: string; subscription_id?: number }> => {
    const response = await apiClient.post('/push/subscribe', subscription);
    return response.data;
  },

  /**
   * Unsubscribe from push notifications
   */
  unsubscribe: async (endpoint: string): Promise<{ status: string; message: string }> => {
    const response = await apiClient.post('/push/unsubscribe', null, { params: { endpoint } });
    return response.data;
  },

  /**
   * Get user's push subscriptions
   */
  getSubscriptions: async (): Promise<PushSubscription[]> => {
    const response = await apiClient.get<PushSubscription[]>('/push/subscriptions');
    return response.data;
  },

  /**
   * Send test notification
   */
  sendTest: async (): Promise<TestNotificationResponse> => {
    const response = await apiClient.post<TestNotificationResponse>('/push/test');
    return response.data;
  },

  /**
   * Delete a subscription
   */
  deleteSubscription: async (subscriptionId: number): Promise<{ status: string; message: string }> => {
    const response = await apiClient.delete(`/push/subscriptions/${subscriptionId}`);
    return response.data;
  },
};

// ==================== PUSH MANAGER UTILITIES ====================

/**
 * Convert a base64 string to Uint8Array (needed for VAPID key)
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Check if push notifications are supported
 */
export function isPushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window;
}

/**
 * Check current push permission status
 */
export function getPushPermission(): NotificationPermission {
  return Notification.permission;
}

/**
 * Request notification permission
 */
export async function requestNotificationPermission(): Promise<NotificationPermission> {
  const permission = await Notification.requestPermission();
  return permission;
}

/**
 * Subscribe to push notifications
 */
export async function subscribeToPush(vapidPublicKey: string): Promise<PushSubscriptionJSON | null> {
  if (!isPushSupported()) {
    console.warn('Push notifications not supported');
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.ready;

    // Check existing subscription
    let subscription = await registration.pushManager.getSubscription();

    if (!subscription) {
      // Create new subscription
      const applicationServerKey = urlBase64ToUint8Array(vapidPublicKey);
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: applicationServerKey as BufferSource,
      });
    }

    return subscription.toJSON();
  } catch (error) {
    console.error('Failed to subscribe to push:', error);
    return null;
  }
}

/**
 * Unsubscribe from push notifications
 */
export async function unsubscribeFromPush(): Promise<boolean> {
  if (!isPushSupported()) {
    return false;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();

    if (subscription) {
      await subscription.unsubscribe();
      return true;
    }

    return false;
  } catch (error) {
    console.error('Failed to unsubscribe from push:', error);
    return false;
  }
}

/**
 * Get current push subscription
 */
export async function getCurrentSubscription(): Promise<PushSubscriptionJSON | null> {
  if (!isPushSupported()) {
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    return subscription?.toJSON() || null;
  } catch (error) {
    console.error('Failed to get push subscription:', error);
    return null;
  }
}
